import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Percent, Plus, Search, Tag, Trash2 } from 'lucide-react';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  Spinner,
  TBody,
  TD,
  TDNum,
  TH,
  THead,
  TR,
  Table,
} from '@pnl/ui';
import { PnlApiError } from '@pnl/sdk';
import type { CouponListItem, CreateCouponInput } from '@pnl/shared';
import { api } from '../lib/api';

export function CouponsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [creating, setCreating] = useState(false);

  const couponsQuery = useQuery({
    queryKey: ['coupons', { q, page }],
    queryFn: () => api.listCoupons({ q: q || undefined, page, pageSize: 20 } as never),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => api.deleteCoupon(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  });

  const toggleMut = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.updateCoupon(id, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['coupons'] }),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">M2.7</p>
          <h1 className="font-display text-ink-900 mt-1 text-3xl font-semibold tracking-tight">
            Cupons
          </h1>
          <p className="text-ink-500 mt-1 text-sm">
            Códigos de desconto aplicáveis no checkout. Percentuais ou valor fixo.
          </p>
        </div>
        <Button leadingIcon={<Plus size={14} />} onClick={() => setCreating(true)}>
          Novo cupom
        </Button>
      </div>

      <Card className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="max-w-md flex-1">
            <Input
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
              placeholder="Buscar pelo código…"
              leadingIcon={<Search size={14} />}
            />
          </div>
          <span className="text-ink-500 text-xs">
            {couponsQuery.data ? `${couponsQuery.data.total} cupom(s)` : '—'}
          </span>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {couponsQuery.isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size={24} />
          </div>
        ) : couponsQuery.data && couponsQuery.data.items.length > 0 ? (
          <>
            <Table>
              <THead>
                <TR>
                  <TH>Código</TH>
                  <TH>Desconto</TH>
                  <TH>Market</TH>
                  <TH className="text-right">Usos</TH>
                  <TH>Validade</TH>
                  <TH>Status</TH>
                  <TH className="text-right">Ações</TH>
                </TR>
              </THead>
              <TBody>
                {couponsQuery.data.items.map((c) => (
                  <CouponRow
                    key={c.id}
                    coupon={c}
                    onToggle={(active) => toggleMut.mutate({ id: c.id, active })}
                    onDelete={() => {
                      if (window.confirm(`Excluir/desativar cupom ${c.code}?`)) {
                        deleteMut.mutate(c.id);
                      }
                    }}
                  />
                ))}
              </TBody>
            </Table>
            <div className="border-ink-200 bg-ink-50/50 text-ink-500 flex items-center justify-between border-t px-4 py-3 text-xs">
              <span>
                Página {page} de {Math.max(1, Math.ceil(couponsQuery.data.total / 20))}
              </span>
              <div className="flex gap-1">
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                >
                  Anterior
                </Button>
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setPage((p) => p + 1)}
                  disabled={page >= Math.ceil(couponsQuery.data.total / 20)}
                >
                  Próxima
                </Button>
              </div>
            </div>
          </>
        ) : (
          <EmptyState
            icon={<Tag size={32} />}
            title="Nenhum cupom criado"
            description="Cupons aplicam descontos no checkout dos sites de venda."
            action={<Button onClick={() => setCreating(true)}>Criar primeiro cupom</Button>}
          />
        )}
      </Card>

      {creating && (
        <CouponDrawer
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            qc.invalidateQueries({ queryKey: ['coupons'] });
          }}
        />
      )}
    </div>
  );
}

function CouponRow({
  coupon,
  onToggle,
  onDelete,
}: {
  coupon: CouponListItem;
  onToggle: (active: boolean) => void;
  onDelete: () => void;
}) {
  const valueLabel =
    coupon.discountType === 'PERCENT'
      ? `${coupon.discountValue}%`
      : (coupon.discountValue / 100).toLocaleString('pt-BR', {
          style: 'currency',
          currency: coupon.marketCode === 'FOREX' ? 'USD' : 'BRL',
        });

  return (
    <TR>
      <TD>
        <span className="text-brand-700 font-mono font-semibold">{coupon.code}</span>
      </TD>
      <TD>
        <span className="text-ink-700 inline-flex items-center gap-1 text-sm">
          <Percent size={12} className="text-ink-400" />
          {valueLabel}
          <span className="text-2xs text-ink-400 ml-1">
            ({coupon.discountType === 'PERCENT' ? 'percentual' : 'fixo'})
          </span>
        </span>
      </TD>
      <TD>
        {coupon.marketCode ? (
          <Badge tone={coupon.marketCode === 'B3' ? 'brand' : 'warning'}>{coupon.marketCode}</Badge>
        ) : (
          <span className="text-ink-400 text-xs">qualquer</span>
        )}
      </TD>
      <TDNum>
        {coupon.usedCount}
        {coupon.maxUses ? ` / ${coupon.maxUses}` : ''}
      </TDNum>
      <TD className="text-ink-600 text-xs">
        {coupon.validUntil
          ? `até ${new Date(coupon.validUntil).toLocaleDateString('pt-BR')}`
          : 'sem prazo'}
      </TD>
      <TD>
        <button
          type="button"
          onClick={() => onToggle(!coupon.active)}
          className={
            'text-2xs rounded-full px-2.5 py-0.5 font-semibold uppercase tracking-wider transition-colors ' +
            (coupon.active
              ? 'bg-success-50 text-success-600 hover:bg-success-100'
              : 'bg-ink-100 text-ink-500 hover:bg-ink-200')
          }
        >
          {coupon.active ? 'Ativo' : 'Inativo'}
        </button>
      </TD>
      <TD className="text-right">
        <Button variant="ghost" size="icon" aria-label="Excluir" onClick={onDelete}>
          <Trash2 size={14} className="text-danger-500" />
        </Button>
      </TD>
    </TR>
  );
}

function CouponDrawer({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [code, setCode] = useState('');
  const [discountType, setDiscountType] = useState<'PERCENT' | 'FIXED'>('PERCENT');
  const [discountValue, setDiscountValue] = useState(10);
  const [validUntil, setValidUntil] = useState('');
  const [maxUses, setMaxUses] = useState('');
  const [marketCode, setMarketCode] = useState<'' | 'B3' | 'FOREX'>('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => {
      const input: CreateCouponInput = {
        code: code.toUpperCase(),
        discountType,
        discountValue: discountType === 'FIXED' ? Math.round(discountValue * 100) : discountValue,
        ...(validUntil && { validUntil: new Date(validUntil).toISOString() }),
        ...(maxUses && { maxUses: parseInt(maxUses, 10) }),
        ...(marketCode && { marketCode }),
        active: true,
      };
      return api.createCoupon(input);
    },
    onSuccess: () => onSaved(),
    onError: (err) => {
      if (err instanceof PnlApiError) {
        setError(
          err.api.fields ? Object.values(err.api.fields).flat().join(' · ') : err.api.message,
        );
      } else {
        setError('Erro de conexão.');
      }
    },
  });

  return (
    <div
      className="bg-ink-900/40 animate-fade-in fixed inset-0 z-50 flex justify-end backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="shadow-lift h-full w-full max-w-md overflow-y-auto bg-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="border-ink-200 border-b px-6 py-4">
          <p className="text-2xs text-brand-500 font-semibold uppercase tracking-widest">
            Novo cupom
          </p>
          <h2 className="font-display text-ink-900 mt-1 text-xl font-semibold">
            Criar código de desconto
          </h2>
        </div>

        <form
          className="space-y-4 p-6"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            mutation.mutate();
          }}
        >
          {error && <Alert tone="danger">{error}</Alert>}

          <Field label="Código" required hint="Letras, números, _ e -. Ex: PROMO10">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              required
              minLength={3}
              maxLength={40}
              pattern="[A-Z0-9_\\-]+"
            />
          </Field>

          <Field label="Tipo de desconto" required>
            <select
              value={discountType}
              onChange={(e) => setDiscountType(e.target.value as 'PERCENT' | 'FIXED')}
              className="form-select border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4"
            >
              <option value="PERCENT">Percentual (%)</option>
              <option value="FIXED">Valor fixo</option>
            </select>
          </Field>

          <Field
            label={discountType === 'PERCENT' ? 'Percentual (1–100)' : 'Valor (em reais ou dólar)'}
            required
          >
            <Input
              type="number"
              value={discountValue}
              onChange={(e) => setDiscountValue(Number(e.target.value))}
              required
              min={discountType === 'PERCENT' ? 1 : 0.01}
              max={discountType === 'PERCENT' ? 100 : undefined}
              step={discountType === 'PERCENT' ? 1 : 0.01}
            />
          </Field>

          <Field label="Restrito a (opcional)" hint="Deixe em branco para qualquer market">
            <select
              value={marketCode}
              onChange={(e) => setMarketCode(e.target.value as '' | 'B3' | 'FOREX')}
              className="form-select border-ink-200 text-ink-900 shadow-soft focus:border-brand-500 focus:ring-brand-500/20 w-full rounded-lg border bg-white px-3 py-2.5 text-sm focus:ring-4"
            >
              <option value="">Qualquer market</option>
              <option value="B3">Apenas B3</option>
              <option value="FOREX">Apenas Forex</option>
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Válido até" hint="Opcional">
              <Input
                type="date"
                value={validUntil}
                onChange={(e) => setValidUntil(e.target.value)}
              />
            </Field>
            <Field label="Limite de usos" hint="Opcional">
              <Input
                type="number"
                value={maxUses}
                onChange={(e) => setMaxUses(e.target.value)}
                min={1}
              />
            </Field>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={mutation.isPending}>
              Criar cupom
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
